import { Injectable } from '@angular/core';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Store } from '@ngxs/store';
import { Observable, Subject } from 'rxjs';
import { take } from 'rxjs/operators';
import { ApiMethod, GetURL, PostURL } from 'src/app/common/const/requests-methods';
import { ICurrentFocusLevel } from 'src/app/common/models/IFamilyTreeTrail';
import { HttpService } from 'src/app/common/services/http.service';
import {
    ClearFamilyTrailData, ResetFamilyTrailData, SetCreateUpdate, SetCurrentFocusLevel, SetFamilyTrailData,
    SetFamilyTreeNodeData, SetFamilyView, SetFamilyVisitedProfile, SetFocusedTrailData, SetTrailHistory, SetTrailLevelChangeHistory
} from
    'src/app/state/family-tree/family-tree.actions';
import { FamilyTreeState, FamilyTreeStateModel } from 'src/app/state/family-tree/family-tree.state';
import { environment } from 'src/environments/environment';

@Injectable({
    providedIn: 'root'
})
export class FamilyTreeService {

    @SelectSnapshot(FamilyTreeState.getFamilyDataState) familyTreeSnapshot: FamilyTreeStateModel | null;

    public recordSelf = new Subject<any>();

    recordSelf$: Observable<string> = this.recordSelf.asObservable();

    constructor(private httpService: HttpService,
        private store: Store) { }

    public getFamilyTreeData(userIds, currentFocusNodeIds): Observable<any> {
        return this.httpService.
            requestCall(environment.apiUrl + PostURL.GET_FAMILY_TREE_CONTENT, ApiMethod.POST, {}, {
                userId: userIds,
                currentFocusNodeId: currentFocusNodeIds
            });
    }

    public getFamilyTreeNodePicture(id) {
        if (id) {
            return environment.apiUrl + GetURL.GET_FAMILY_TREE_NODE_PICTURE + '?id=' + id;
        }
    }

    public addSmartFamily(dataIN) {
      return this.httpService.requestCall(environment.apiUrl + PostURL.ADD_SMART_FAMILY, ApiMethod.POST, {}, dataIN);
    }

    public addFamilyTree(dataIn): Observable<any> {
        return this.httpService.
            requestCall(environment.apiUrl + PostURL.ADD_FAMILY_TREE_NODE, ApiMethod.POST, {}, dataIn);
    }

    public updateFamilyTree(dataIn): Observable<any> {
        return this.httpService.
            requestCall(environment.apiUrl + PostURL.UPDATE_FAMILY_TREE_NODE, ApiMethod.POST, {}, dataIn);
    }

    public getAllFamilyTreeNode(): Observable<any> {
        return this.httpService.requestCall(environment.apiUrl + GetURL.GET_ALL_FAMILY_TREE_NODE, ApiMethod.GET);
    }

    public getFamilyTrail() {
        return this.familyTreeSnapshot.familyTrail;
    }

    public getFocusedTrail() {
        return this.familyTreeSnapshot.focusedTrail;
    }

    public getFamilyTreeNodeData() {
        return this.familyTreeSnapshot.data;
    }

    public getFamilyCreatedUpdated() {
        return this.familyTreeSnapshot.createUpdate;
    }

    public getFamilyVisitedProfile() {
        return this.familyTreeSnapshot.visitedProfile;
    }


    public setFamilyTreeTrial(newTrail) {
        let trails = this.getFamilyTrail();
        const existingTrail = trails.findIndex(trail => trail?.id === newTrail?.id);

        if (trails?.length > 0 && existingTrail === -1) {
            trails = [...trails, newTrail];
            this.setTrails(trails);
            this.setFocusedTrail(trails[trails?.length - 1]);
            this.maintainHistory(newTrail);
        } else {
            if (trails?.length === 0) {
                trails = [...[], newTrail];
                this.setTrails([newTrail]);
                this.setFocusedTrail(trails[trails?.length - 1]);
                this.maintainHistory(newTrail);
            }
            if (existingTrail > -1) {
                this.removeFromFamilyTree(existingTrail + 1);
            }
        }

    }

    public setTrails(value) {
        this.store.dispatch(new SetFamilyTrailData(value));
    }

    public setFocusedTrail(focusedTrail) {
        this.store.dispatch(new SetFocusedTrailData(focusedTrail));
    }

    public setCreateUpdate(type: string) {
        return this.store.dispatch(new SetCreateUpdate(type));
    }

    public setFamilyVisitedProfile(flag: boolean) {
        return this.store.dispatch(new SetFamilyVisitedProfile(flag));
    }

    public setFamilyTreeNodeData(value) {
        this.store.dispatch(new SetFamilyTreeNodeData(value)).subscribe();
    }

    public setFamilSelectedView(value) {
        this.store.dispatch(new SetFamilyView(value)).pipe(take(1)).subscribe();
    }

    public setTrailHistory(value) {
        this.store.dispatch(new SetTrailHistory(value)).pipe(take(1)).subscribe();
    }

    public setTrailLevelChangeHistory(value) {
        this.store.dispatch(new SetTrailLevelChangeHistory(value)).pipe(take(1)).subscribe();
    }

    public setCurrentFocusLevel(value) {
        this.store.dispatch(new SetCurrentFocusLevel(value)).pipe(take(1)).subscribe();
    }

    public resetHistories() {
        this.store.dispatch(new ResetFamilyTrailData()).pipe(take(1)).subscribe();
    }

    public removeFromFamilyTree(index) {
        const trails = [...this.getFamilyTrail()];
        trails.splice(index, trails.length);
        this.setTrails(trails);
        this.setFocusedTrail(trails[trails?.length - 1]);
    }

    public clearFamilyTreeState() {
        this.store.dispatch(new ClearFamilyTrailData());
    }

    public changeFromTrailHistoryDialog(level, newTrail) {
        const trailHistory = { ...this.familyTreeSnapshot?.trailHistory };
        const levelToChange = JSON.parse(JSON.stringify(trailHistory[level]));
        const findIndex = levelToChange.findIndex(trail => trail?.id === newTrail?.id);
        if (findIndex > -1) {
            levelToChange.splice(findIndex);
        }
        this.setTrails(JSON.parse(JSON.stringify(levelToChange)));
        const focused: ICurrentFocusLevel = {};
        focused[level] = JSON.parse(JSON.stringify(trailHistory[level]))[0];
        this.setCurrentFocusLevel(focused);
        this.setFamilyTreeTrial(newTrail);
    }

    public changeFamilyTreeByProfile(newTrail) {
        let trailHistory = { ...this.familyTreeSnapshot?.trailHistory };
        const currentFocusLevel = this.familyTreeSnapshot.currentFocusLevel;
        const currentLevel = Object.keys(currentFocusLevel)[0];
        const findParentNodeIdIndex = trailHistory[currentLevel]
            .findIndex(trail => trail?.parentNodeId === newTrail?.parentNodeId);

        if (findParentNodeIdIndex > -1) {
            const trailLevelChangeHistory = { ...this.familyTreeSnapshot?.trailLevelChangeHistory };
            if (trailLevelChangeHistory.hasOwnProperty(currentLevel)) {
                const trailLevelHistoryData = trailLevelChangeHistory[currentLevel];
                if (Object.keys(trailLevelHistoryData)?.length > 0) {
                    const existingLevelHistoryTrail = trailHistory[currentLevel]
                        .findIndex(trail => trail?.id === trailLevelHistoryData?.id);
                    if (findParentNodeIdIndex >= existingLevelHistoryTrail) {
                        const clevel = Number(currentLevel.substring(6));
                        const sortedTrailHistory1 = Object.keys(trailHistory)?.map(level => Number(level.substring(6))).sort();
                        sortedTrailHistory1.forEach(level => {
                            if (level > clevel) {
                                delete trailHistory[`level-${level}`];
                                delete trailLevelChangeHistory[`level-${level}`];
                            }

                        });
                        delete trailLevelChangeHistory[currentLevel];
                        this.setTrailLevelChangeHistory(trailLevelChangeHistory);
                    }
                }
            }
            let history = [...trailHistory[currentLevel]];
            history.splice(findParentNodeIdIndex, trailHistory[currentLevel]?.length);
            const existingTrail1 = trailHistory[currentLevel].findIndex(trail => trail?.id === newTrail?.id);
            if (existingTrail1 === -1) {
                history = [...history, newTrail];
                trailHistory[currentLevel] = [...history];
                this.setTrailHistory(trailHistory);
            }

        }


        trailHistory = { ...this.familyTreeSnapshot?.trailHistory };

        const allLevelsChangeHistory = { ...this.familyTreeSnapshot?.trailLevelChangeHistory };
        const sortedTrailHistory = Object.keys(trailHistory)?.map(level => Number(level.substring(6))).sort();
        const currentLevelN = Object.keys(currentFocusLevel)[0];
        const currentHistory = [...trailHistory[currentLevelN]];
        const existingTrail = currentHistory.findIndex(trail => trail?.id === newTrail?.id);
        if (existingTrail === -1) {
            currentHistory.push(newTrail);
            trailHistory[currentLevelN] = [...currentHistory];
        }
        allLevelsChangeHistory[currentLevelN] = newTrail;
        const newLevel = 'level-' + (sortedTrailHistory.pop() + 1);
        trailHistory[newLevel] = [];
        trailHistory[newLevel].push(newTrail);
        this.setTrails([]);
        this.setFamilyTreeTrial(newTrail);
        this.setTrailHistory(trailHistory);
        this.setTrailLevelChangeHistory(allLevelsChangeHistory);
        const focused: ICurrentFocusLevel = {};
        focused[newLevel] = newTrail;
        this.setCurrentFocusLevel(focused);
    }

    private maintainHistory(newTrail) {
        const trailHistory = { ...this.familyTreeSnapshot?.trailHistory };
        const currentFocusLevel = this.familyTreeSnapshot.currentFocusLevel;

        if (Object.keys(trailHistory)?.length === 0) {
            trailHistory['level-1'] = [];
            trailHistory['level-1'].push(newTrail);
            this.setTrailHistory(trailHistory);
            this.setCurrentFocusLevel({ 'level-1': newTrail });
        } else {
            const currentLevel = Object.keys(currentFocusLevel)[0];
            const existingTrail = trailHistory[currentLevel].findIndex(trail => trail?.id === newTrail?.id);
            const findParentNodeIdIndex = trailHistory[currentLevel]
                .findIndex(trail => trail?.parentNodeId === newTrail?.parentNodeId);

            if (findParentNodeIdIndex > -1) {
                const trailLevelChangeHistory = { ...this.familyTreeSnapshot?.trailLevelChangeHistory };
                if (trailLevelChangeHistory.hasOwnProperty(currentLevel)) {
                    const trailLevelHistoryData = trailLevelChangeHistory[currentLevel];
                    if (Object.keys(trailLevelHistoryData)?.length > 0) {
                        const existingLevelHistoryTrail = trailHistory[currentLevel]
                            .findIndex(trail => trail?.id === trailLevelHistoryData?.id);
                        if (findParentNodeIdIndex >= existingLevelHistoryTrail) {
                            const clevel = Number(currentLevel.substring(6));
                            const sortedTrailHistory = Object.keys(trailHistory)?.map(level => Number(level.substring(6))).sort();
                            sortedTrailHistory.forEach(level => {
                                if (level > clevel) {
                                    delete trailHistory[`level-${level}`];
                                    delete trailLevelChangeHistory[`level-${level}`];
                                }

                            });
                            delete trailLevelChangeHistory[currentLevel];
                            this.setTrailLevelChangeHistory(trailLevelChangeHistory);
                        }
                    }
                }
                let history = [...trailHistory[currentLevel]];
                history.splice(findParentNodeIdIndex, trailHistory[currentLevel]?.length);

                if (existingTrail === -1) {
                    history = [...history, newTrail];
                    trailHistory[currentLevel] = [...history];
                    this.setTrailHistory(trailHistory);
                }

            } else {
                if (trailHistory[currentLevel]?.length > 0 && existingTrail === -1) {
                    trailHistory[currentLevel] = [...trailHistory[currentLevel], newTrail];
                    this.setTrailHistory(trailHistory);
                }
            }


        }

    }

}
